// Dominio de medidas físicas de producto — modo "Catálogo de muebles con IA".
//
// Fuente de verdad: la columna `productos.medidas` (jsonb). Ver ADR-583.
//
// POR QUÉ ESTE MÓDULO IMPORTA MÁS DE LO QUE PARECE:
// el problema #1 que reporta el cliente (y que el deck de Sodimac pone como
// motivo de existir) son los "errores de escala/dimensiones". Estas funciones
// producen el texto que se le manda al modelo de imagen para que respete las
// proporciones. Si `proporcionLegible` devuelve algo mal, el mueble sale
// deformado. Todo acá es función pura y sin dependencias a propósito.
//
// NOTA SOBRE TESTS: el repo no tiene Vitest ni ningún test (verificado en
// project/, premiere-plugin/, remotion/ y la raíz). Se decidió no introducirlo
// en esta rama. Por eso los casos borde quedan documentados como comentarios
// arriba de cada función, con ejemplos concretos de entrada → salida.

export type UnidadMedida = 'cm' | 'mm' | 'm';

export type OrigenMedidas = 'tiendanube' | 'usuario' | 'ia';

export interface Medidas {
  unidad: UnidadMedida;
  alto?: number | null;
  ancho?: number | null;
  profundidad?: number | null;
  /** Relevante en cerámicas, tableros, alfombras. */
  espesor?: number | null;
  peso_kg?: number | null;
  /**
   * De dónde salió el dato. `tiendanube` / `ia` = lo dedujo una máquina y hay
   * que mostrárselo al cliente como revisable; `usuario` = lo confirmó él y es
   * fuente de verdad.
   */
  origen?: OrigenMedidas;
  /** ISO 8601. */
  actualizado?: string;
}

/** Las tres que definen el volumen y que el prompt necesita para la proporción. */
const DIMENSIONES_PRINCIPALES = ['alto', 'ancho', 'profundidad'] as const;

export const UNIDAD_POR_DEFECTO: UnidadMedida = 'cm';

/** Factor de conversión a centímetros, la unidad canónica interna. */
const A_CM: Record<UnidadMedida, number> = { mm: 0.1, cm: 1, m: 100 };

const esNumeroUtil = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v) && v > 0;

/**
 * `true` si hay al menos una de alto/ancho/profundidad cargada.
 *
 * Se usa para el guard de la vista Infografía: sin ninguna medida no tiene
 * sentido generarla. Deliberadamente NO exige las tres — con alto y ancho ya se
 * puede armar una infografía útil (ej. una alfombra o una cerámica, donde la
 * profundidad no aplica).
 *
 *   tieneMedidas({unidad:'cm', alto:60})                → true
 *   tieneMedidas({unidad:'cm', alto:0})                 → false  (0 no sirve)
 *   tieneMedidas({unidad:'cm', peso_kg:12})             → false  (peso no es dimensión)
 *   tieneMedidas(null)                                  → false
 */
export const tieneMedidas = (m?: Medidas | null): boolean =>
  !!m && DIMENSIONES_PRINCIPALES.some((k) => esNumeroUtil(m[k]));

/** `true` si están las tres dimensiones — habilita la proporción completa. */
export const tieneMedidasCompletas = (m?: Medidas | null): boolean =>
  !!m && DIMENSIONES_PRINCIPALES.every((k) => esNumeroUtil(m[k]));

/** Convierte a centímetros. Devuelve null si el valor no sirve. */
export function aCentimetros(
  valor: number | null | undefined,
  unidad: UnidadMedida,
): number | null {
  if (!esNumeroUtil(valor)) return null;
  return valor * A_CM[unidad];
}

/**
 * Las mismas medidas en otra unidad (el peso no cambia). Para sumar una medida nueva en `m` a
 * otras guardadas en `cm` sin re-etiquetarlas: 180 cm no pasa a ser 180 m
 * (ADR-2026-10-19-revision-greptile-pdf-y-catalogo).
 */
export function convertirMedidas(m: Medidas, a: UnidadMedida): Medidas {
  if (m.unidad === a) return m;
  const pasar = (v: number | null | undefined): number | null | undefined =>
    // Cifras significativas, no decimales: 0,3 mm en metros es 0,0003 y con 3 decimales daba 0 (y
    // la medida se borraba); sin redondear, 180 cm daban 1,8000000000000003 m
    // (ADR-2026-10-19-revision-greptile-pdf-y-catalogo-2).
    esNumeroUtil(v) ? Number(((v * A_CM[m.unidad]) / A_CM[a]).toPrecision(10)) : v;
  return { ...m, unidad: a, alto: pasar(m.alto), ancho: pasar(m.ancho), profundidad: pasar(m.profundidad), espesor: pasar(m.espesor) };
}

/**
 * Relación de proporción normalizada, en el formato que entiende mejor un modelo
 * de imagen que los números absolutos.
 *
 * Se normaliza sobre el **ancho** (= 1) porque es la dimensión que el modelo usa
 * como referencia horizontal del encuadre. Si no hay ancho, cae a la primera
 * dimensión disponible para no perder la información.
 *
 * Se redondea a 2 decimales: más precisión no aporta nada al prompt y ensucia.
 *
 *   {unidad:'cm', alto:60, ancho:47, profundidad:39}
 *     → "alto:ancho:profundidad = 1.28 : 1 : 0.83"
 *   {unidad:'cm', alto:60, ancho:47}
 *     → "alto:ancho = 1.28 : 1"
 *   {unidad:'mm', alto:600, ancho:470}
 *     → "alto:ancho = 1.28 : 1"   (la unidad se cancela al ser una razón)
 *   {unidad:'cm', alto:60}
 *     → null   (una sola dimensión no es una proporción)
 */
export function proporcionLegible(m?: Medidas | null): string | null {
  if (!m) return null;

  const presentes = DIMENSIONES_PRINCIPALES.filter((k) => esNumeroUtil(m[k]));
  if (presentes.length < 2) return null;

  // La unidad se cancela en una razón, así que no hace falta convertir; pero se
  // convierte igual por si en el futuro se mezclan unidades por dimensión.
  const base = esNumeroUtil(m.ancho)
    ? (m.ancho as number)
    : (m[presentes[0]] as number);
  if (!esNumeroUtil(base)) return null;

  const etiquetas = presentes.join(':');
  const valores = presentes
    .map((k) => {
      const ratio = (m[k] as number) / base;
      // `Number()` saca los ceros de cola: 1.00 → "1", 0.83 → "0.83"
      return String(Number(ratio.toFixed(2)));
    })
    .join(' : ');

  return `${etiquetas} = ${valores}`;
}

/**
 * Texto humano de una medida, para la UI y para el prompt.
 *
 *   formatearMedida(60, 'cm')    → "60 cm"
 *   formatearMedida(60.5, 'cm')  → "60,5 cm"   (coma decimal, es-AR)
 *   formatearMedida(null, 'cm')  → null
 */
export function formatearMedida(
  valor: number | null | undefined,
  unidad: UnidadMedida,
): string | null {
  if (!esNumeroUtil(valor)) return null;
  // Hasta 2 decimales, sin ceros de relleno. `toLocaleString('es-AR')` da la
  // coma decimal que espera el usuario.
  const n = Number(valor.toFixed(2));
  return `${n.toLocaleString('es-AR')} ${unidad}`;
}

/**
 * Resumen de una línea, para chips y para el bloque `<medidas>` del prompt.
 *
 *   {unidad:'cm', alto:60, ancho:47, profundidad:39}
 *     → "Alto 60 cm · Ancho 47 cm · Profundidad 39 cm"
 *   {unidad:'cm'} → null
 */
export function resumenMedidas(m?: Medidas | null): string | null {
  if (!m) return null;
  const partes: string[] = [];
  const etiquetas: Record<string, string> = {
    alto: 'Alto',
    ancho: 'Ancho',
    profundidad: 'Profundidad',
    espesor: 'Espesor',
  };
  for (const k of [...DIMENSIONES_PRINCIPALES, 'espesor'] as const) {
    const txt = formatearMedida(m[k], m.unidad);
    if (txt) partes.push(`${etiquetas[k]} ${txt}`);
  }
  return partes.length ? partes.join(' · ') : null;
}

/**
 * Parsea lo que el usuario escribe en el input de una medida.
 *
 * Tolerante a propósito: la gente copia y pega de la ficha de su e-commerce y
 * escribe cualquier cosa. Devuelve null si no hay un número reconocible, para
 * que el campo quede vacío en vez de guardar basura.
 *
 *   parsearMedida("60")       → 60
 *   parsearMedida("60,5")     → 60.5    (coma decimal)
 *   parsearMedida("60.5")     → 60.5    (punto decimal)
 *   parsearMedida("60 cm")    → 60      (se ignora la unidad escrita)
 *   parsearMedida("60cm")     → 60
 *   parsearMedida(" 60 ")     → 60
 *   parsearMedida("")         → null
 *   parsearMedida("ancho")    → null
 *   parsearMedida("-5")       → null    (una medida negativa no existe)
 *   parsearMedida("0")        → null    (0 se trata como "sin dato")
 *
 * OJO con el caso "1.200" (mil doscientos con separador de miles): se
 * interpreta como 1.2, no como 1200. Es ambiguo sin contexto y el caso real
 * —una medida de más de mil— es raro en cm; si aparece, resolverlo acá.
 */
export function parsearMedida(input: string | null | undefined): number | null {
  if (typeof input !== 'string') return null;
  const limpio = input.trim().replace(',', '.');
  if (!limpio) return null;

  // Primer número del string, con decimales opcionales. Ignora la unidad
  // pegada ("60cm") y cualquier texto alrededor.
  const match = limpio.match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;

  const n = Number(match[0]);
  return esNumeroUtil(n) ? n : null;
}

/**
 * Normaliza el jsonb que viene de la base, que puede tener cualquier cosa
 * (lo escribe n8n con un LLM y los LLM devuelven strings donde esperás números).
 *
 *   normalizarMedidas({unidad:'cm', alto:'60'})  → {unidad:'cm', alto:60}
 *   normalizarMedidas({alto:60})                 → {unidad:'cm', alto:60}  (default)
 *   normalizarMedidas({unidad:'pulgadas'})       → unidad cae a 'cm'
 *   normalizarMedidas(null)                      → null
 */
export function normalizarMedidas(raw: unknown): Medidas | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;

  const unidadRaw = typeof o.unidad === 'string' ? o.unidad : '';
  const unidad: UnidadMedida =
    unidadRaw === 'mm' || unidadRaw === 'm' || unidadRaw === 'cm'
      ? unidadRaw
      : UNIDAD_POR_DEFECTO;

  const num = (v: unknown): number | null => {
    if (typeof v === 'number') return esNumeroUtil(v) ? v : null;
    if (typeof v === 'string') return parsearMedida(v);
    return null;
  };

  const origenRaw = o.origen;
  const origen: OrigenMedidas | undefined =
    origenRaw === 'tiendanube' || origenRaw === 'usuario' || origenRaw === 'ia'
      ? origenRaw
      : undefined;

  return {
    unidad,
    alto: num(o.alto),
    ancho: num(o.ancho),
    profundidad: num(o.profundidad),
    espesor: num(o.espesor),
    peso_kg: num(o.peso_kg),
    origen,
    actualizado: typeof o.actualizado === 'string' ? o.actualizado : undefined,
  };
}

/**
 * Bloque que se manda a n8n dentro del payload de generación. Va en las TRES
 * vistas, no solo en la infografía: la slide 9 del deck de Sodimac muestra que
 * la foto en contexto también se rompe por escala (una cortina mal dimensionada
 * dentro de una habitación). Ver ADR-584.
 *
 * Devuelve `null` si no hay nada útil que mandar, para no ensuciar el payload
 * con un objeto vacío.
 */
export function medidasParaPayload(
  m?: Medidas | null,
): { medidas: Medidas; proporcion: string | null; resumen: string | null } | null {
  if (!tieneMedidas(m)) return null;
  return {
    medidas: m as Medidas,
    proporcion: proporcionLegible(m),
    resumen: resumenMedidas(m),
  };
}
